import { useEffect, useRef, type ComponentProps } from "react";

type LoopVideoProps = Omit<
  ComponentProps<"video">,
  "src" | "autoPlay" | "muted" | "loop" | "playsInline" | "ref"
> & {
  /** Leave out until the video should load. */
  src?: string;
  playing: boolean;
};

/** Muted looping video that plays only while `playing` and frees its source on unmount. */
export default function LoopVideo({
  src,
  playing,
  preload = "metadata",
  ...props
}: LoopVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);

  // src is set here rather than as a prop so the cleanup can release it.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return;
    video.src = src;
    return () => {
      video.pause();
      video.removeAttribute("src");
      video.load();
    };
  }, [src]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return;
    if (playing) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, [src, playing]);

  return (
    <video
      ref={videoRef}
      autoPlay={playing}
      muted
      loop
      playsInline
      preload={preload}
      {...props}
    />
  );
}
